import FineUploaderS3 from 'fine-uploader-wrappers/s3';
import RSVP from 'rsvp';
import { isHeicFile, convertHeicToJpeg } from '../../utilities/heic';
import {
  classifyUploadResponse,
  reportAttachmentUploadFailure,
  uploaderKeyFor,
} from '../../../shared/upload_error_reporting';

export const trixFileAttachmentMix = {
  components: {
    FineUploaderS3,
    RSVP,
  },
  props: {
    accessKey: {
      type: String,
      required: false,
    },
    authToken: {
      type: String,
      required: false,
    },
    cloudfrontBaseUrl: {
      type: String,
      required: false,
    },
    endpoint: {
      type: String,
      required: false,
    },
  },
  data() {
    const vm = this;
    const getKey = function (fileId) {
      const filename = vm.uploader.methods.getName(fileId);
      const keyRetrieval = new RSVP.Promise((resolve, reject) => {
        const params = {
          attachment: {
            name: filename,
            prefix: 'documents/',
          },
        };
        const url = vm.$apiService.attachmentsUrl();
        axios.post(url, params)
          .then((response) => {
            vm.pendingKey = response.data.key;
            resolve(response.data.key);
          })
          .catch((error) => {
            reject(error);
          });
      });
      return keyRetrieval;
    };
    const getContentType = function (fileId) {
      const file = vm.uploader.methods.getFile(fileId);
      return (file && file.type) || 'application/octet-stream';
    };
    const uploader = new FineUploaderS3({
      options: {
        request: {
          endpoint: vm.endpoint,
          accessKey: vm.accessKey,
        },
        maxConnections: 1,
        signature: {
          endpoint: '/api/v1/attachments/s3_signature',
          customHeaders: {
            Authorization: `Bearer ${vm.authToken}`,
          },
        },
        uploadSuccess: {
          endpoint: '/api/v1/attachments/s3_confirm',
          customHeaders: {
            Authorization: `Bearer ${vm.authToken}`,
          },
        },
        objectProperties: {
          // acl: 'public-read',
          key: getKey,
          contentType: getContentType,
        },
        chunking: {
          enabled: true,
        },
        resume: {
          // Resume is now disabled because the UI is not handling it correctly
          // to see the bug start an upload with pause enabled, then refresh the page
          // now try to upload again and you will see that the video does not start uploading
          enabled: false,
        },
      },
    });
    return {
      pendingAttachment: null,
      pendingKey: null,
      queuedAttachments: [],
      uploader,
    };
  },
  mounted() {
    const vm = this;
    vm.uploader.on('complete', (id, name, response) => {
      // FineUploader fires `complete` even when the upload failed at the s3_confirm step
      // (the file reached S3 but our confirm endpoint returned non-2xx). In that case it
      // sets response.success === false. Previously we ignored `response` entirely and always
      // reported success, which embedded a dead CloudFront URL and toasted "Attachment uploaded"
      // for an attachment the server never marked as uploaded (the "saved but blank" bug).
      const attachment = vm.pendingAttachment;
      const expectedKey = vm.pendingKey;
      vm.pendingAttachment = null;

      // 'succeeded' | 'failed' | 'ambiguous' -- see classifyUploadResponse. Staying
      // lenient on 'ambiguous' preserves the previous behaviour (a 2xx/no-body confirm
      // must still count as success) without letting that case go unrecorded.
      const outcome = classifyUploadResponse(response);
      const reportContext = {
        source: 'trix_file_attachment_mix',
        fineUploaderId: id,
        fileName: name,
        expectedKey,
        uploaderKey: uploaderKeyFor(vm.uploader, id),
        response,
      };

      if (attachment && outcome !== 'failed') {
        if (outcome === 'ambiguous') {
          // We're treating this as a success, but we can't actually tell. Record it
          // so the size of this blind spot is visible in the data.
          reportAttachmentUploadFailure({ ...reportContext, reason: 'ambiguous_complete_response' });
        } else if (reportContext.uploaderKey && reportContext.uploaderKey !== expectedKey) {
          // Confirm succeeded, but pendingKey is a single shared slot while key
          // retrieval is per-file and async -- so the URL we're embedding may belong
          // to another file. Report it; the attachment still uploaded fine.
          reportAttachmentUploadFailure({ ...reportContext, reason: 'key_mismatch_on_success' });
        }

        // handle completed upload
        vm.$notificationManager.emit('attachment-uploaded', id);
        attachment.setUploadProgress(100);
        const url = `${vm.cloudfrontBaseUrl}/${expectedKey}`;
        attachment.setAttributes({
          url,
          href: url,
        });
        vm.$notificationManager.emit('show-toast', 'Attachment uploaded', true);
      } else if (attachment) {
        reportAttachmentUploadFailure({ ...reportContext, reason: 'confirm_rejected' });
        // Remove the broken attachment from the editor so the user can retry instead of
        // being told it saved when it didn't.
        attachment.remove();
        // NOTE: `$notificationManager` is a plain event bus (shared/event_bus.js) with
        // `emit`, not Vue's `$emit`. Calling `$emit` here threw a TypeError, which
        // swallowed the toast *and* skipped uploadAttachments() below, stalling the queue.
        vm.$notificationManager.emit('show-toast', 'Attachment failed to upload, please try again', false);
      }
      vm.uploadAttachments();
    });

    vm.uploader.on('statusChange', (id, oldStatus, newStatus) => {
      console.log(`status changed on uploader from ${oldStatus} to ${newStatus}`);
    });

    document.addEventListener('trix-file-accept', vm.trixFileAccept);
    document.addEventListener('trix-attachment-add', vm.trixAttachmentAdd);
  },
  destroyed() {
    const vm = this;
    document.removeEventListener('trix-file-accept', vm.trixFileAccept);
    document.removeEventListener('trix-attachment-add', vm.trixAttachmentAdd);
  },
  methods: {
    trixFileAccept(event) {
      if (event.file.type.includes('video')) {
        event.preventDefault();
        alert('Video files are not allowed as attachments. Please contact support@wrestlingiq.com if you need assistance.');
      }

      // 15MB file limit on attachments
      if (event.file.size > 15728640) {
        event.preventDefault();
        alert('Attachments have a 15MB file size limit. Please contact support@wrestlingiq.com if you need assistance.');
      }
    },
    trixAttachmentAdd(event) {
      const { attachment } = event;
      if (attachment.file) {
        // we enforce our own queue, instead of relying on fineuploaders funky status changes
        this.queuedAttachments.push(attachment);
        this.uploadAttachments();
      }
    },
    uploadAttachments() {
      const vm = this;
      // only kick off another upload if one isn't currently uploading
      if (vm.pendingAttachment) {
        return;
      }

      const nextAttachment = vm.queuedAttachments.pop();
      if (nextAttachment) {
        vm.pendingAttachment = nextAttachment;
        const { file } = nextAttachment;
        // Browsers other than Safari can't render HEIC, so convert to JPEG before
        // uploading. On conversion failure fall back to the original file.
        const filePromise = isHeicFile(file)
          ? convertHeicToJpeg(file).catch(() => file)
          : Promise.resolve(file);
        filePromise.then((f) => vm.uploader.methods.addFiles([f]));
      }
    },
  },
};
